// Simple in-memory fake API for PV Design requests/results
// Persists to localStorage per project where possible

import localPVData from "../data/pvDataLocal";

const storageKey = (projectId) => `pvDesign:${projectId}`;

const loadState = (projectId) => {
  try {
    const raw = localStorage.getItem(storageKey(projectId));
    if (!raw) return { requests: [], resultsByRequestId: {} };
    return JSON.parse(raw);
  } catch {
    return { requests: [], resultsByRequestId: {} };
  }
};

const saveState = (projectId, state) => {
  try {
    localStorage.setItem(storageKey(projectId), JSON.stringify(state));
  } catch {}
};

const randomId = () => Math.random().toString(36).slice(2);

const groupBy = (arr, key) => arr.reduce((acc, item) => { const k = item[key]; (acc[k] ||= []).push(item); return acc; }, {});

const normalizeLocalPanels = () => {
  const raw = Array.isArray(localPVData) ? localPVData : [];
  return raw.map((e, idx) => {
    const polygonSrc = Array.isArray(e.pv_panel_polygon) ? e.pv_panel_polygon : [];
    const polygon = [
      ...(polygonSrc[0] || []),
      ...(polygonSrc[1] || []),
      ...(polygonSrc[2] || []),
      ...(polygonSrc[2] || []),
      ...(polygonSrc[3] || []),
      ...(polygonSrc[4] || polygonSrc[0] || []),
    ];
    return {
      id: e.id ?? idx,
      polygon,
      annual_yield: Number(e.annual_yield || 0),
      perM2: Number(e.area || 0),
      zone: String(e.zone ?? "Scenario_0"),
    };
  });
};

const generateDesigns = () => {
  const normalized = normalizeLocalPanels();
  const byZone = groupBy(normalized, "zone");
  const designs = Object.entries(byZone).map(([zone, panels]) => ({
    id: randomId(),
    name: `${zone}`,
    panels,
  }));
  return designs.length ? designs : [{ id: randomId(), name: "Scenario_0", panels: [] }];
};

export const pvDesignApi = {
  async listRequests({ projectId }) {
    const state = loadState(projectId);
    return new Promise((resolve) => {
      setTimeout(() => resolve(state.requests), 250);
    });
  },
  async createRequest({ projectId, name, params }) {
    const state = loadState(projectId);
    const req = {
      id: randomId(),
      name,
      createdAt: new Date().toISOString(),
      status: "pending",
      recordsCount: 0,
      params,
    };
    state.requests = [req, ...state.requests];
    saveState(projectId, state);
    return new Promise((resolve) => {
      setTimeout(() => resolve(req), 400);
    });
  },
  async getResults({ projectId, requestId }) {
    const state = loadState(projectId);
    let designs = state.resultsByRequestId[requestId];
    if (!designs) {
      designs = generateDesigns();
      state.resultsByRequestId[requestId] = designs;
      const idx = state.requests.findIndex((r) => r.id === requestId);
      if (idx >= 0) {
        state.requests[idx] = {
          ...state.requests[idx],
          status: "done",
          recordsCount: designs.reduce((s, d) => s + (d.panels?.length || 0), 0),
        };
      }
      saveState(projectId, state);
    }
    return new Promise((resolve) => {
      setTimeout(() => resolve(designs), 500);
    });
  },
  async removePanels({ projectId, requestId, panelIds }) {
    const state = loadState(projectId);
    const designs = state.resultsByRequestId[requestId] || [];
    const updated = designs.map((d) => ({
      ...d,
      panels: (d.panels || []).filter((p) => !panelIds.includes(p.id)),
    }));
    state.resultsByRequestId[requestId] = updated;
    const idx = state.requests.findIndex((r) => r.id === requestId);
    if (idx >= 0) {
      state.requests[idx] = {
        ...state.requests[idx],
        recordsCount: updated.reduce((s, d) => s + (d.panels?.length || 0), 0),
      };
    }
    saveState(projectId, state);
    return new Promise((resolve) => setTimeout(() => resolve({ ok: true }), 250));
  },
};


